/* chainRelationships.js — Deterministic chain composer.
 *
 * UK property transactions sit inside a buyer→seller chain. Until
 * the backend exposes a first-class `chain_id` for v2 deals, we
 * group active deals into deterministic 3-deal chains so the
 * ChainRibbon always has something calm to render.
 *
 * Doctrine:
 *   - Deterministic, never noisy.
 *   - Reuse `exchangeReadiness.js` — no second logic system.
 *   - Operator vocabulary only (Under Offer / AML / Conveyancing
 *     / Pre-Exchange / Exchange + Moving Well / Requires Attention).
 */
import { computeExchangeReadiness } from "./exchangeReadiness";

// Stage → operator-facing 5-bucket label. Mirrors STAGE_BUCKET
// in MissionControlPage / SalesProgression / Deal Command.
const STAGE_BUCKET = {
  offer:          "Under Offer",
  memo:           "AML",
  aml:            "AML",
  solicitors:     "Conveyancing",
  mortgage_app:   "Conveyancing",
  survey:         "Conveyancing",
  searches:       "Conveyancing",
  enquiries:      "Conveyancing",
  mortgage_offer: "Pre-Exchange",
  exchange_ready: "Pre-Exchange",
  exchanged:      "Exchange",
  completed:      "Exchange",
};

// Tiny deterministic hash so the same deal always lands in the
// same chain bucket across renders + pages.
function _hash(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Resolve the chain that contains the given deal.
 *
 * Returns `{ peers: [{deal, position, readiness}], position }`
 * where `peers` is the ordered chain (bottom → middle → top) and
 * `position` is this deal's slot in the chain.
 *
 * @param {Object} deal       — the deal we're rendering for.
 * @param {Array}  allDeals   — every deal in the user's workspace.
 */
export function resolveChain(deal, allDeals) {
  if (!deal || !Array.isArray(allDeals) || allDeals.length === 0) {
    return { peers: [], position: "middle" };
  }

  // Only build chains across active (non-archived) deals — terminal
  // / archived deals don't operationally block exchange any more.
  const active = allDeals.filter((d) => !d.archived);
  if (active.length === 0) return { peers: [], position: "middle" };

  // Sort deterministically by id so the bucket is stable.
  const sorted = [...active].sort((a, b) => (a.id < b.id ? -1 : 1));

  // The chain that contains *this* deal: bucket of 3 around its
  // index in the sorted list. Bottom (buyer side), middle (this),
  // top (seller side).
  const idx = sorted.findIndex((d) => d.id === deal.id);
  if (idx < 0) return { peers: [], position: "middle" };

  const chainSize = Math.min(3, sorted.length);
  // Centre the chain on the current deal where possible.
  let start = Math.max(0, idx - 1);
  if (start + chainSize > sorted.length) start = sorted.length - chainSize;

  const slice = sorted.slice(start, start + chainSize);
  const peers = slice.map((d, i) => {
    const positionLabel =
      chainSize === 1 ? "single" :
      i === 0          ? "bottom" :
      i === chainSize - 1 ? "top" :
                            "middle";
    const isThis = d.id === deal.id;
    const readiness = computeExchangeReadiness(d, d.activity || []);
    return {
      deal: d,
      position: positionLabel,
      isThis,
      readiness,
      stageBucket: STAGE_BUCKET[d.stage] || "Under Offer",
    };
  });

  const thisPeer = peers.find((p) => p.isThis);
  return {
    peers,
    position: thisPeer?.position || "middle",
    weakestLink: peers.reduce((worst, p) =>
      (!worst || p.readiness.score < worst.readiness.score) ? p : worst, null),
  };
}

/**
 * Aggregate chain status for a single chain.
 *   Stable        → no requires-attention peer, weakest >= 50
 *   Slowing       → weakest in `Awaiting *` state
 *   Requires Attention → any peer at Requires Attention
 */
export function chainSignal(peers) {
  if (!peers || peers.length === 0) return { tone: "calm", label: "No chain" };
  const anyAttention = peers.some((p) => p.readiness.state === "Requires Attention");
  if (anyAttention) return { tone: "warn", label: "Requires Attention" };
  const anyAwaiting = peers.some((p) => /Awaiting/.test(p.readiness.state));
  if (anyAwaiting) return { tone: "amber", label: "Progress Slowing" };
  return { tone: "calm", label: "Stable" };
}

/**
 * Pick the "most urgent" active deal across the workspace — the
 * one closest to its target exchange but not yet exchanged. Used
 * as the anchor for the ChainRibbon on workspace-level pages
 * (Mission Control, Deals) so the ribbon surfaces the chain
 * most likely to require active coordination today.
 */
export function pickAnchorDeal(allDeals) {
  if (!Array.isArray(allDeals) || allDeals.length === 0) return null;
  const now = Date.now();
  const candidates = allDeals
    .filter((d) => !d.archived && d.stage !== "exchanged" && d.stage !== "completed")
    .filter((d) => d.targetExchange)
    .map((d) => ({ d, days: (new Date(d.targetExchange).getTime() - now) / 86_400_000 }))
    .filter((x) => !Number.isNaN(x.days))
    .sort((a, b) => Math.abs(a.days) - Math.abs(b.days));
  return candidates[0]?.d || null;
}
